const ASSETS = 'https://prospecting.semdeck.com/mockup-assets/electrical';

const DEFAULT_COPY = {
  eyebrow: 'Residential And Commercial Electricians',
  headline: 'Electrical Work Done Right Across Northeast Ohio',
  tagline: 'We help homeowners and small businesses with repairs, upgrades, lighting, panels, and safety fixes. You get clear communication, careful work, and a crew that respects the property.',
  about: 'COMPANY_NAME helps local homeowners, storefronts, offices, and small commercial properties keep power safe, reliable, and ready for daily use. The best electrical websites make urgent calls easy, explain services clearly, and build trust before someone picks up the phone.',
  services: [
    { name: 'Panel Upgrades', desc: 'Upgrade old panels, add capacity for modern appliances, and make the electrical system easier to maintain.' },
    { name: 'Repairs And Troubleshooting', desc: 'Track down flickering lights, tripping breakers, dead outlets, and other problems before they become bigger issues.' },
    { name: 'Lighting And Fixtures', desc: 'Install recessed lighting, exterior lighting, ceiling fans, switches, dimmers, and fixtures with a clean finish.' },
    { name: 'EV Chargers And Circuits', desc: 'Add dedicated circuits, garage power, EV chargers, and outlets where the home or business actually needs them.' },
  ],
};

const DEFAULT_HERO_USPS = [
  'Licensed, insured, and ready for code-conscious work',
  'Fast help for outlets, breakers, lighting, and panel issues',
  'Transparent estimates before the work begins',
  'Serving Cleveland, Akron, Lorain, Medina, and nearby suburbs',
];

const SERVICE_ICONS = ['⚡', '⏚', '◉', '▣'];

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function phoneDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function clampServices(copy) {
  return Array.isArray(copy.services) && copy.services.length
    ? copy.services.slice(0, 4)
    : DEFAULT_COPY.services;
}

function buildBrandMark(company, logoUrl) {
  if (logoUrl) {
    return `<img class="cs-logo-img" src="${escapeHtml(logoUrl)}" alt="${company} logo" width="250" height="72" decoding="async">`;
  }

  return `<span class="mockup-wordmark" aria-label="${company}">
            <span class="mockup-wordmark-icon" aria-hidden="true">⚡</span>
            <span class="mockup-wordmark-text">${company}</span>
          </span>`;
}

function buildHeroUsps(items) {
  const usps = Array.isArray(items) && items.length ? items.slice(0, 4) : DEFAULT_HERO_USPS;
  return usps.map((item) => `<li>${escapeHtml(item)}</li>`).join('');
}

function buildServiceItems(services) {
  return services.map((service, index) => `
                    <li class="cs-item">
                        <span class="cs-icon" aria-hidden="true">${SERVICE_ICONS[index] || SERVICE_ICONS[0]}</span>
                        <h3 class="cs-h3">${escapeHtml(service.name || DEFAULT_COPY.services[index]?.name || 'Electrical Service')}</h3>
                        <p class="cs-item-text">${escapeHtml(service.desc || DEFAULT_COPY.services[index]?.desc || 'Reliable electrical service for safer, easier-to-use power.')}</p>
                    </li>`).join('');
}

export function generateElectricalMockup(templateId, data = {}) {
  if (templateId !== 11) return '<p>Unknown electrical template ID</p>';

  const copy = data.copy || {};
  const company = escapeHtml(data.company || 'Your Electrical Company');
  const phone = escapeHtml(data.phone || '(216) 555-0198');
  const email = escapeHtml(data.email || 'service@example.com');
  const digits = phoneDigits(phone);
  const services = clampServices(copy);
  const eyebrow = escapeHtml(copy.eyebrow || DEFAULT_COPY.eyebrow);
  const headline = escapeHtml(copy.headline || DEFAULT_COPY.headline).replace(/\n/g, '<br>');
  const tagline = escapeHtml(copy.tagline || DEFAULT_COPY.tagline);
  const about = escapeHtml((copy.about || DEFAULT_COPY.about).replace('COMPANY_NAME', company));
  const logoUrl = data.logoUrl && String(data.logoUrl).trim() ? String(data.logoUrl).trim() : '';
  const brandMark = buildBrandMark(company, logoUrl);
  const serviceItems = buildServiceItems(services);
  const heroUsps = buildHeroUsps(copy.usps || copy.heroUsps);

  return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="robots" content="noindex, nofollow">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <meta name="description" content="${company} electrical website mockup by SEMdeck.">
    <title>Home | ${company}</title>
    <link rel="icon" type="image/svg+xml" href="${ASSETS}/logo.svg">
    <link rel="stylesheet" href="${ASSETS}/root.css">
    <link rel="stylesheet" href="${ASSETS}/critical.css">
    <link rel="stylesheet" href="${ASSETS}/local.css">
    <style>
      .mockup-wordmark { display: inline-flex; align-items: center; gap: 0.75rem; color: var(--headerColor); font-family: var(--headingFont); font-weight: 900; line-height: 1.05; max-width: 18rem; }
      .mockup-wordmark-icon { width: 3rem; height: 3rem; border-radius: 0.75rem; background: var(--secondaryDark); color: var(--primary); display: inline-flex; align-items: center; justify-content: center; font-size: 1.5rem; flex: none; }
      .mockup-wordmark-text { display: block; font-size: clamp(1rem, 1.4vw, 1.18rem); letter-spacing: 0; }
      #cs-footer-841 .mockup-wordmark { color: #fff; }
      #hero-2140 .cs-picture-main img { object-position: center; }
    </style>
</head>
<body>
    <a class="skip" href="#main">Click To Skip To Main Content</a>

    <header id="cs-navigation" aria-label="site navigation">
        <div class="cs-top-bar">
            <div class="cs-top-container">
                <a href="tel:${digits}" class="cs-top-link">${phone}</a>
                <a href="mailto:${email}" class="cs-top-link">${email}</a>
            </div>
        </div>
        <div class="cs-container">
            <a href="#" class="cs-logo" aria-label="Back to home">${brandMark}</a>
            <button class="cs-toggle" aria-label="mobile menu toggle" aria-expanded="false"><span class="cs-line"></span><span class="cs-line"></span><span class="cs-line"></span></button>
            <div class="cs-ul-wrapper">
                <nav><ul class="cs-ul"><li class="cs-li"><a href="#" class="cs-li-link cs-active">Home</a></li><li class="cs-li"><a href="#about-2143" class="cs-li-link">About</a></li><li class="cs-li"><a href="#services-2141" class="cs-li-link">Services</a></li><li class="cs-li"><a href="#cta-2148" class="cs-li-link">Contact</a></li></ul></nav>
                <a href="#cta-2148" class="cs-button-solid">Schedule Service</a>
            </div>
        </div>
    </header>

    <main id="main">
        <section id="hero-2140">
            <div class="cs-background" aria-hidden="true"></div>
            <div class="cs-container">
                <div class="cs-content">
                    <span class="cs-topper">${eyebrow}</span>
                    <h1 class="cs-title">${headline}</h1>
                    <p class="cs-text">${tagline}</p>
                    <ul class="cs-hero-list">${heroUsps}</ul>
                    <div class="cs-button-group"><a href="tel:${digits}" class="cs-button-solid">Call ${phone}</a><a href="#services-2141" class="cs-button-transparent">View Services</a></div>
                </div>
                <div class="cs-hero-visual" aria-label="Electrical service highlights">
                    <picture class="cs-picture cs-picture-main"><img src="${ASSETS}/images/electrical-stock/electric-hero.jpg" alt="Electrician working on residential wiring" width="1730" height="1170" loading="eager" decoding="async"></picture>
                    <div class="cs-trust-card"><span class="cs-number">24hr</span><span class="cs-desc">Emergency Calls</span></div>
                    <div class="cs-mini-card"><span class="cs-icon" aria-hidden="true">✓</span><span>Clean installs and careful troubleshooting</span></div>
                </div>
            </div>
        </section>

        <section id="services-2141">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">Electrical Services</span><h2 class="cs-title">The Work People Call About Most</h2><p class="cs-text">This template keeps service cards simple and easy to scan. Each service can become a long-form SEO page later without making the homepage feel crowded.</p></div>
                <ul class="cs-card-group">${serviceItems}</ul>
            </div>
        </section>

        <section id="proof-2142">
            <div class="cs-container"><div class="cs-stat"><span class="cs-number">4.9★</span><span class="cs-desc">Review Placeholder</span></div><div class="cs-stat"><span class="cs-number">24hr</span><span class="cs-desc">Emergency Availability</span></div><div class="cs-stat"><span class="cs-number">NEO</span><span class="cs-desc">Local Service Area</span></div><div class="cs-stat"><span class="cs-number">100%</span><span class="cs-desc">Insured Crew</span></div></div>
        </section>

        <section id="about-2143">
            <div class="cs-container">
                <div class="cs-image-group"><picture class="cs-picture cs-picture-large"><img src="${ASSETS}/images/electrical-stock/electric-panel.jpg" alt="Electrician inspecting an electrical panel" width="1812" height="1210" loading="lazy" decoding="async"></picture><picture class="cs-picture cs-picture-small"><img src="${ASSETS}/images/electrical-stock/electric-work.jpg" alt="Electrician completing electrical service work" width="1219" height="813" loading="lazy" decoding="async"></picture></div>
                <div class="cs-content"><span class="cs-topper">Local Electrical Help</span><h2 class="cs-title">Built For Homes, Shops, And Small Commercial Spaces</h2><p class="cs-text">${about}</p><p class="cs-text">The design keeps trust signals visible, uses strong contrast, and gives service pages an obvious path for SEO content around panel upgrades, electrical repairs, lighting, generators, and EV chargers.</p><a href="#cta-2148" class="cs-button-solid">More About The Company</a></div>
            </div>
        </section>

        <section id="emergency-2144">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">When Power Problems Cannot Wait</span><h2 class="cs-title">Emergency Electrical Service Without The Guesswork</h2><p class="cs-text">Power outages, burning smells, sparking outlets, and unsafe panels need a calm next step. This section gives the company a strong emergency-service message without making claims that should be verified first.</p><a href="tel:${digits}" class="cs-button-solid">Call Now</a></div>
                <ul class="cs-list"><li>Breaker panels and tripped circuits</li><li>Dead outlets and partial power loss</li><li>Storm-related electrical issues</li><li>Safety checks before repairs continue</li></ul>
            </div>
        </section>

        <section id="process-2145">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">How It Works</span><h2 class="cs-title">A Simple Path From Problem To Fix</h2><p class="cs-text">Electrical service can feel stressful. The process section keeps the buyer oriented and makes the next step feel easy.</p></div>
                <ol class="cs-card-group"><li class="cs-item"><span class="cs-step">01</span><h3 class="cs-h3">Tell Us What Is Happening</h3><p class="cs-item-text">Share the issue, the property type, and whether it feels urgent so the service call starts with the right context.</p></li><li class="cs-item"><span class="cs-step">02</span><h3 class="cs-h3">Inspect And Explain</h3><p class="cs-item-text">The electrician checks the system, explains the options, and gives a clear recommendation before work begins.</p></li><li class="cs-item"><span class="cs-step">03</span><h3 class="cs-h3">Fix It Cleanly</h3><p class="cs-item-text">The crew completes the work, cleans the area, and makes sure the customer understands what changed.</p></li></ol>
            </div>
        </section>

        <section id="areas-2146">
            <div class="cs-container"><div class="cs-content"><span class="cs-topper">Service Areas</span><h2 class="cs-title">Electrical Service Across Northeast Ohio</h2><p class="cs-text">This template can adapt to a single-city contractor or a wider regional company. Add or remove cities based on the real service area.</p></div><div class="cs-area-grid"><span>Cleveland</span><span>Lakewood</span><span>Parma</span><span>Strongsville</span><span>Medina</span><span>Akron</span><span>Lorain</span><span>Mentor</span></div></div>
        </section>

        <section id="reviews-2147">
            <div class="cs-container"><div class="cs-content"><span class="cs-topper">Testimonials</span><h2 class="cs-title">What Customers Could Say</h2></div><ul class="cs-card-group"><li class="cs-item"><span class="cs-stars">★★★★★</span><p class="cs-review">Lorem ipsum dolor sit amet, consectetur adipiscing elit. Integer posuere erat a ante venenatis dapibus posuere velit aliquet.</p><span class="cs-name">Customer Name</span><span class="cs-job">Homeowner</span></li><li class="cs-item"><span class="cs-stars">★★★★★</span><p class="cs-review">Vestibulum id ligula porta felis euismod semper. Donec ullamcorper nulla non metus auctor fringilla.</p><span class="cs-name">Customer Name</span><span class="cs-job">Property Manager</span></li><li class="cs-item"><span class="cs-stars">★★★★★</span><p class="cs-review">Cras mattis consectetur purus sit amet fermentum. Maecenas faucibus mollis interdum.</p><span class="cs-name">Customer Name</span><span class="cs-job">Business Owner</span></li></ul></div>
        </section>

        <section id="cta-2148">
            <div class="cs-container">
                <div class="cs-content"><span class="cs-topper">Schedule Electrical Service</span><h2 class="cs-title">Need An Electrician In Northeast Ohio?</h2><p class="cs-text">Call or send a message. Use this area for a quick quote form, emergency phone CTA, or simple contact block.</p></div>
                <form class="cs-form" name="electrical-contact" method="post" data-netlify="true"><input type="hidden" name="form-name" value="electrical-contact"><label>Name<input type="text" name="name" placeholder="Your Name" required></label><label>Email<input type="email" name="email" placeholder="you@example.com" required></label><label>Phone<input type="tel" name="phone" placeholder="${phone}"></label><label>How can we help?<textarea name="message" placeholder="Tell us what is going on"></textarea></label><button class="cs-button-solid" type="submit">Request Service</button></form>
            </div>
        </section>
    </main>

    <footer id="cs-footer-841">
        <div class="cs-container"><div class="cs-logo-group"><a href="#" class="cs-logo" aria-label="Back to home">${brandMark}</a><p class="cs-text">Residential and light commercial electrical service for Northeast Ohio.</p></div><div class="cs-nav-group"><span class="cs-header">Quick Links</span><ul class="cs-nav-list"><li><a href="#" class="cs-nav-link">Home</a></li><li><a href="#about-2143" class="cs-nav-link">About</a></li><li><a href="#services-2141" class="cs-nav-link">Services</a></li><li><a href="#cta-2148" class="cs-nav-link">Contact</a></li></ul></div><div class="cs-contact-group"><span class="cs-header">Contact</span><ul class="cs-contact-list"><li><a href="tel:${digits}" class="cs-contact-link">${phone}</a></li><li><a href="mailto:${email}" class="cs-contact-link">${email}</a></li></ul></div></div>
        <div class="cs-bottom"><span class="cs-copyright">&copy; 2026 ${company}. All Rights Reserved.</span><span class="cs-credit">Designed and Developed by <a href="https://semdeck.com" target="_blank" rel="noopener">SEMdeck</a></span></div>
    </footer>
    <script>
      const nav = document.getElementById('cs-navigation');
      const toggle = document.querySelector('#cs-navigation .cs-toggle');
      if (toggle) toggle.addEventListener('click', () => { const open = nav.classList.toggle('cs-active'); toggle.setAttribute('aria-expanded', open); });
      window.addEventListener('scroll', () => document.body.classList.toggle('scroll', window.scrollY > 50));
      const scrollTopButton = document.createElement('button');
      scrollTopButton.className = 'cs-scroll-top';
      scrollTopButton.type = 'button';
      scrollTopButton.setAttribute('aria-label', 'Scroll to top');
      scrollTopButton.innerHTML = '<span aria-hidden="true">↑</span>';
      document.body.appendChild(scrollTopButton);
      const toggleScrollTopButton = () => scrollTopButton.classList.toggle('cs-visible', window.scrollY > 500);
      scrollTopButton.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      window.addEventListener('scroll', toggleScrollTopButton);
      toggleScrollTopButton();
    </script>
</body>
</html>`;
}
